const getCellValueByRef = (
  ref,
  sheetData,
  visitedCells = new Set(),
  currentCell = null
) => {
  const match = ref.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return 0;
  const colStr = match[1].toUpperCase();
  const row = parseInt(match[2], 10) - 1;

  let col = 0;
  for (let i = 0; i < colStr.length; i++) {
    col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
  }
  col -= 1;

  const cellRef = `${colStr}${row + 1}`;
  if (currentCell && cellRef === currentCell) {
    throw new Error(`Circular dependency detected in ${currentCell}`);
  }
  if (visitedCells.has(cellRef)) {
    throw new Error(`Circular reference: ${cellRef}`);
  }

  const cell = sheetData?.[row]?.[col];
  if (!cell || typeof cell.formula !== 'string') {
    const val = Number(cell?.value);
    return isNaN(val) ? 0 : val;
  }

  return evaluateSheetFormula(cell.formula, sheetData, cellRef, visitedCells);
};

const evaluateExpression = (expr, sheetData, visitedCells, currentCell) => {
  expr = expr.trim();

  if (/^(SUM|SUMME|MULTIPLY|SUBTRACT|DIVIDE|RUNDEN)\s*\(/i.test(expr)) {
    return evaluateSheetFormula(
      '=' + expr,
      sheetData,
      currentCell,
      visitedCells
    );
  }

  const replaced = expr.replace(/[A-Z]+\d+/gi, (ref) =>
    getCellValueByRef(ref, sheetData, visitedCells, currentCell)
  );

  try {
    return new Function(`return ${replaced}`)();
  } catch {
    return 0;
  }
};

const splitArguments = (argString) => {
  const args = [];
  let current = '';
  let depth = 0;
  for (let char of argString) {
    if (char === ',' && depth === 0) {
      args.push(current);
      current = '';
    } else {
      if (char === '(') depth++;
      else if (char === ')') depth--;
      current += char;
    }
  }
  if (current) args.push(current);
  return args;
};

const getValuesFromRange = (rangeStr, sheetData, visitedCells, currentCell) => {
  const match = rangeStr.match(/^([A-Z]+\d+):([A-Z]+\d+)$/i);
  if (!match) return [];

  const getCoord = (ref) => {
    const colStr = ref.match(/[A-Z]+/i)[0].toUpperCase();
    const row = parseInt(ref.match(/\d+/)[0], 10) - 1;

    let col = 0;
    for (let i = 0; i < colStr.length; i++) {
      col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
    }
    col -= 1;
    return { row, col };
  };

  const start = getCoord(match[1]);
  const end = getCoord(match[2]);
  const values = [];

  for (let r = start.row; r <= end.row; r++) {
    for (let c = start.col; c <= end.col; c++) {
      const colLabel = getColumnLabel(c);
      const ref = `${colLabel}${r + 1}`;
      values.push(getCellValueByRef(ref, sheetData, visitedCells, currentCell));
    }
  }

  return values;
};

export const getColumnLabel = (index) => {
  const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let label = '';
  do {
    label = A[index % 26] + label;
    index = Math.floor(index / 26) - 1;
  } while (index >= 0);
  return label;
};

export const evaluateSheetFormula = (
  formula,
  sheetData,
  currentCell = null,
  visitedCells = new Set()
) => {
  try {
    if (typeof formula !== 'string' || !formula.startsWith('=')) return formula;

    if (currentCell) visitedCells.add(currentCell);

    const parsed = formula.slice(1).trim().toUpperCase();

    const extractArgs = (type) => {
      const inside = formula.slice(type.length + 2, -1);
      const rawArgs = splitArguments(inside);

      return rawArgs.flatMap((arg) => {
        arg = arg.trim();
        if (/^[A-Z]+\d+:[A-Z]+\d+$/i.test(arg)) {
          return getValuesFromRange(
            arg,
            sheetData,
            new Set(visitedCells),
            currentCell
          );
        } else {
          return [
            evaluateExpression(
              arg,
              sheetData,
              new Set(visitedCells),
              currentCell
            ),
          ];
        }
      });
    };

    if (parsed.startsWith('SUM(') || parsed.startsWith('SUMME(')) {
      const args = extractArgs(parsed.startsWith('SUM(') ? 'SUM' : 'SUMME');
      return args.reduce((a, b) => a + b, 0);
    }

    if (parsed.startsWith('MULTIPLY(')) {
      const args = extractArgs('MULTIPLY');
      return args.reduce((a, b) => a * b, 1);
    }

    if (parsed.startsWith('SUBTRACT(')) {
      const args = extractArgs('SUBTRACT');
      return args.reduce((a, b) => a - b);
    }

    if (parsed.startsWith('DIVIDE(')) {
      const args = extractArgs('DIVIDE');
      return args.reduce((a, b) => a / b);
    }

    if (parsed.startsWith('RUNDEN(')) {
      const args = extractArgs('RUNDEN');
      const [value, digits = 0] = args;
      return Number(Number(value).toFixed(digits));
    }

    return evaluateExpression(parsed, sheetData, visitedCells, currentCell);
  } catch (err) {
    return err.message || 'ERR';
  }
};

export const parseRange = (rangeStr) => {
  const parts = rangeStr.split(':');
  if (parts.length === 1) {
    const cell = parseCell(parts[0]);
    return {
      start: cell,
      end: cell,
    };
  }

  return {
    start: parseCell(parts[0]),
    end: parseCell(parts[1]),
  };
};

export const parseCell = (cell) => {
  const match = cell.match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;

  const col = match[1];
  const row = parseInt(match[2], 10);

  return {
    colIndex: columnToIndex(col),
    rowIndex: row - 1,
  };
};

const columnToIndex = (col) => {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 64);
  }
  return index - 1;
};

export const setCursorAtLast = (el) => {
  el.focus();
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};
export const extractCellRefsFromFormula = (formula) => {
  if (!formula || typeof formula !== 'string')
    return { cellRefs: [], rangeRefs: [] };

  // Capitalize formula entirely
  formula = formula.toUpperCase();

  if (!formula.startsWith('=')) return { cellRefs: [], rangeRefs: [] };

  // Allowed characters in formula
  const allowed = /^[=A-Z0-9\+\-\*\/\(\)\:\,\;\. ]+$/;
  if (!allowed.test(formula)) return { cellRefs: [], rangeRefs: [] };

  // Extract all references: A1 or A1:B2
  const matches = formula.match(/[A-Z]+[0-9]+(:[A-Z]+[0-9]+)?/g);

  const cellRefs = [];
  const rangeRefs = [];

  if (matches) {
    for (const ref of matches) {
      if (ref.includes(':')) {
        rangeRefs.push(ref);
      } else {
        cellRefs.push(ref);
      }
    }
  }

  return { cellRefs, rangeRefs };
};

export const extractCellRefsFromRange = (rangeRef) => {
  const { start, end } = parseRange(rangeRef);

  const cellRefs = [];

  const rowStart = Math.min(start.rowIndex, end.rowIndex);
  const rowEnd = Math.max(start.rowIndex, end.rowIndex);
  const colStart = Math.min(start.colIndex, end.colIndex);
  const colEnd = Math.max(start.colIndex, end.colIndex);

  for (let row = rowStart; row <= rowEnd; row++) {
    for (let col = colStart; col <= colEnd; col++) {
      const colLabel = getColumnLabel(col);
      const rowLabel = `${row + 1}`;
      cellRefs.push(`${colLabel}${rowLabel}`);
    }
  }

  return cellRefs;
};

export const getCellHighlightBoxStyle = (
  start,
  end,
  cellWidth = 120,
  cellHeight = 22,
  offsetX = 50,
  offsetY = 24
) => {
  const top = Math.min(start.rowIndex, end.rowIndex) * cellHeight + offsetY;
  const left = Math.min(start.colIndex, end.colIndex) * cellWidth + offsetX;

  const width = (Math.abs(end.colIndex - start.colIndex) + 1) * cellWidth;
  const height = (Math.abs(end.rowIndex - start.rowIndex) + 1) * cellHeight;

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    height: `${height}px`,
  };
};

export const getNavigateCellCoordinate = (
  pressKey,
  activeCellPosition,
  sheetDimensions
) => {
  let rowIndex = activeCellPosition?.rowIndex;
  let colIndex = activeCellPosition?.colIndex;
  if (
    rowIndex === null ||
    colIndex === null ||
    !sheetDimensions?.rows ||
    !sheetDimensions?.cols
  )
    return null;
  const totalRows = sheetDimensions.rows - 1;
  const totalCols = sheetDimensions.cols - 1;
  switch (pressKey) {
    case 'ArrowUp':
      rowIndex = rowIndex > 0 ? rowIndex - 1 : rowIndex;
      break;
    case 'ArrowDown':
      rowIndex = rowIndex < totalRows ? rowIndex + 1 : rowIndex;
      break;
    case 'ArrowLeft':
      colIndex = colIndex > 0 ? colIndex - 1 : colIndex;
      break;
    case 'ArrowRight':
      colIndex = colIndex < totalCols ? colIndex + 1 : colIndex;
      break;
  }
  return { rowIndex, colIndex };
};

export const isSpecialCharAtEnd = (content) => {
  if (typeof content !== 'string') return false;
  const specialChars = ['+', '=', ',', '/', '%', '^', '*', '(', '-', ';'];
  const lastChar = content.trim().slice(-1);
  return specialChars.includes(lastChar);
};

export const getLastCellMatch = (content) => {
  if (typeof content !== 'string') return false;
  const refPatternAtEnd = /([A-Z]+\d+(?::[A-Z]+\d+)?)$/i;
  const match = content.trim().match(refPatternAtEnd);
  return match;
};

export const getLastCellReference = (formula) => {
  try {
    if (typeof formula !== 'string' || !formula.startsWith('=')) return null;

    const trimmed = formula.trim();

    if (trimmed === '=' || isSpecialCharAtEnd(trimmed)) return '';

    const match = getLastCellMatch(trimmed);
    if (match && match.length) {
      const cellRef = match[1];
      if (cellRef.includes(':')) {
        return parseRange(cellRef);
      } else {
        return parseCell(cellRef);
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};
